import type { SxProps, Theme } from "@mui/material";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { type DropdownItem, SystemDropdown } from "../../UIToolkit/Inputs/SystemDropDown";
import { HolidayDayPartLabel } from "./HolidayDayPartLabel";
import { HOLIDAY_DAY_PARTS, type HolidayDayPart } from "./holidayApi";
import { DAY_PART_LABEL_KEY, NO_DAY_PART_KEY } from "./holidayFormat";

type Props = {
	/** Null = none yet (no shifts in the last 6 weeks). */
	value: HolidayDayPart | null;
	onChange: (dayPart: HolidayDayPart) => void;
	/** Visible label of a form field; omit for an inline select named by `ariaLabel`. */
	label?: string;
	ariaLabel: string;
	disabled?: boolean;
	sx?: SxProps<Theme>;
	dataTestId: string;
};

/** Day, Evening or Night, each with its glyph. */
export const HolidayDayPartSelect: FC<Props> = ({ value, onChange, label, ariaLabel, disabled, sx, dataTestId }) => {
	const { t } = useTranslation();
	const items = useMemo<DropdownItem<HolidayDayPart>[]>(
		() =>
			HOLIDAY_DAY_PARTS.map((dayPart) => ({ value: dayPart, label: t(DAY_PART_LABEL_KEY[dayPart]), icon: <HolidayDayPartLabel dayPart={dayPart} iconOnly /> })),
		[t],
	);
	return (
		<SystemDropdown<DropdownItem<HolidayDayPart>>
			items={items}
			selectedItem={value ?? undefined}
			onChange={(next) => {
				if (next !== undefined && next !== value) onChange(next);
			}}
			label={label}
			fieldLabel={ariaLabel}
			placeholder={value === null ? t(NO_DAY_PART_KEY) : undefined}
			disabled={disabled}
			formControlSx={sx}
			search={false}
			data-testid={dataTestId}
		/>
	);
};
